import type { CreationTask } from '../api/creations'
import { chromeTimelineKpiChipShellClass } from '../components/ChromeSurface'
import type {
  TimelineOutputSuperResolution,
  TimelineRound,
} from '../types/timeline'
import { createLogger } from './logger'
import type { TimelineRoundStatus } from './timelineStem'
import { sanitizeUserFacingErrorMessage } from './userFacingErrorMessage'

const timelinePresentationLogger = createLogger('create-page-timeline')

/** 发送前可取消的等待时长（毫秒） */
export const CREATE_PAGE_GENERATION_DEFER_MS = 3000

export function waitCreatePageDeferMs(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(new DOMException('Aborted', 'AbortError'))
      return
    }
    const id = window.setTimeout(() => {
      signal.removeEventListener('abort', onAbort)
      resolve()
    }, ms)
    const onAbort = () => {
      window.clearTimeout(id)
      reject(new DOMException('Aborted', 'AbortError'))
    }
    signal.addEventListener('abort', onAbort, { once: true })
  })
}

export function isDeferringTimelineRound(round: TimelineRound): boolean {
  if (
    round.status === 'CANCELLED' ||
    round.status === 'FAILED' ||
    round.status === 'SUCCEEDED' ||
    round.status === 'UNAVAILABLE'
  ) {
    return false
  }
  return typeof round.pendingSubmitDeadline === 'number' && Date.now() < round.pendingSubmitDeadline
}

/** 仍等待出图（延迟窗结束后、已发起后端任务且尚未返回） */
export function isRoundAwaitingGeneration(round: TimelineRound): boolean {
  if (isDeferringTimelineRound(round)) return false
  if (
    round.status === 'FAILED' ||
    round.status === 'SUCCEEDED' ||
    round.status === 'UNAVAILABLE' ||
    round.status === 'CANCELLED'
  ) {
    return false
  }
  return round.items.length === 0
}

export function shouldIgnoreTimelinePullStart(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false
  return Boolean(
    target.closest(
      'button, a[href], input, textarea, select, [contenteditable="true"], [role="button"]',
    ),
  )
}

export function normalizeCreationTaskError(task: CreationTask, error: unknown): string {
  const raw = task.errorMessage?.trim()
    ? task.errorMessage.trim()
    : error instanceof Error && error.message.trim()
      ? error.message.trim()
      : ''
  if (!raw) return '生成失败，请稍后重试'
  const sanitized = sanitizeUserFacingErrorMessage(raw)
  const out = sanitized.trim() || '生成失败，请稍后重试'
  if (import.meta.env.DEV && out !== raw) {
    timelinePresentationLogger.debug('生图错误原始信息（内部）', raw)
  }
  return out
}

export function outputSuperResolutionTimelineChip(
  sr: TimelineOutputSuperResolution | undefined,
  angular: boolean,
): {
  label: string
  className: string
  title?: string
} | null {
  if (!sr?.attempted) return null
  const arr = sr.perImageSuccess
  const shell = chromeTimelineKpiChipShellClass(angular)

  const okChip = `${shell} border-[color-mix(in_srgb,var(--timeline-rail-success)_26%,var(--line)_74%)] bg-[color-mix(in_srgb,var(--timeline-rail-success)_9%,var(--panel)_91%)] text-[color-mix(in_srgb,var(--timeline-rail-success)_48%,var(--ink)_52%)]`

  if (!arr?.length) {
    return {
      label: '超清',
      title: '已尝试输出超清',
      className: okChip,
    }
  }

  const ok = arr.filter(Boolean).length
  const fail = arr.length - ok

  if (fail === 0) {
    return {
      label: arr.length > 1 ? `超清成功 ${ok}/${arr.length}` : '超清成功',
      title: '全部输出已完成超清',
      className: okChip,
    }
  }
  if (ok === 0) {
    return {
      label: arr.length > 1 ? `超清失败 ${fail}/${arr.length}` : '超清失败',
      title: '输出超清均未成功，已保留原图',
      className: `${shell} border-[color-mix(in_srgb,var(--timeline-rail-danger)_34%,var(--line)_66%)] bg-[color-mix(in_srgb,var(--timeline-rail-danger)_11%,var(--panel)_89%)] text-[color-mix(in_srgb,var(--timeline-rail-danger)_58%,var(--ink)_42%)]`,
    }
  }
  return {
    label: `超清 ${ok}/${arr.length}`,
    title: `部分完成超清：成功 ${ok}，失败 ${fail}`,
    className: `${shell} border-[color-mix(in_srgb,var(--timeline-rail-warn)_36%,var(--line)_64%)] bg-[color-mix(in_srgb,var(--timeline-rail-warn)_11%,var(--panel)_89%)] text-[color-mix(in_srgb,var(--timeline-rail-warn)_54%,var(--ink)_46%)]`,
  }
}

/**
 * 时间线轨与卡片描边：语义色随主题变化（--timeline-rail-*）。
 * 与 {@link episodeQueueTimelineTheme} 队列轨配色同系。
 */
export function timelineRoundAccent(
  deferring: boolean,
  awaitingGen: boolean,
  status: TimelineRoundStatus,
): {
  dot: string
  stem: string
  cardBorder: string
  frameBorder: string
  srStatus: string
} {
  if (deferring) {
    return {
      dot: 'border-[color-mix(in_srgb,var(--timeline-rail-warn)_58%,var(--line)_42%)] bg-[color-mix(in_srgb,var(--timeline-rail-warn)_24%,var(--panel)_76%)]',
      stem: 'bg-[var(--timeline-rail-warn)]',
      cardBorder: 'border-[color-mix(in_srgb,var(--timeline-rail-warn)_56%,var(--line)_44%)]',
      frameBorder:
        'border-[color-mix(in_srgb,var(--timeline-rail-warn)_40%,var(--line)_60%)]',
      srStatus: '待发',
    }
  }
  if (awaitingGen) {
    return {
      dot: 'border-[color-mix(in_srgb,var(--timeline-rail-warn)_62%,var(--line)_38%)] bg-[color-mix(in_srgb,var(--timeline-rail-warn)_30%,var(--panel)_70%)]',
      stem: 'bg-[var(--timeline-rail-warn)]',
      cardBorder: 'border-[color-mix(in_srgb,var(--timeline-rail-warn)_58%,var(--line)_42%)]',
      frameBorder:
        'border-[color-mix(in_srgb,var(--timeline-rail-warn)_45%,var(--line)_55%)]',
      srStatus: '生成中',
    }
  }
  if (status === 'FAILED') {
    return {
      dot: 'border-[color-mix(in_srgb,var(--timeline-rail-danger)_58%,var(--line)_42%)] bg-[color-mix(in_srgb,var(--timeline-rail-danger)_24%,var(--panel)_76%)]',
      stem: 'bg-[var(--timeline-rail-danger)]',
      cardBorder: 'border-[color-mix(in_srgb,var(--timeline-rail-danger)_55%,var(--line)_45%)]',
      frameBorder: 'border-line',
      srStatus: '失败',
    }
  }
  if (status === 'CANCELLED') {
    return {
      dot: 'border-[color-mix(in_srgb,var(--timeline-rail-cancelled)_55%,var(--line)_45%)] bg-[color-mix(in_srgb,var(--timeline-rail-cancelled)_20%,var(--panel)_80%)]',
      stem: 'bg-[var(--timeline-rail-cancelled)]',
      cardBorder: 'border-[color-mix(in_srgb,var(--timeline-rail-cancelled)_52%,var(--line)_48%)]',
      frameBorder: 'border-line',
      srStatus: '已取消',
    }
  }
  if (status === 'UNAVAILABLE') {
    return {
      dot: 'border-[color-mix(in_srgb,var(--timeline-rail-muted)_52%,var(--line)_48%)] bg-[color-mix(in_srgb,var(--timeline-rail-muted)_22%,var(--panel)_78%)]',
      stem: 'bg-[var(--timeline-rail-muted)]',
      cardBorder: 'border-[color-mix(in_srgb,var(--timeline-rail-muted)_55%,var(--line)_45%)]',
      frameBorder: 'border-line',
      srStatus: '无图',
    }
  }
  if (status === 'PENDING') {
    return {
      dot: 'border-[color-mix(in_srgb,var(--timeline-rail-warn)_60%,var(--line)_40%)] bg-[color-mix(in_srgb,var(--timeline-rail-warn)_26%,var(--panel)_74%)]',
      stem: 'bg-[var(--timeline-rail-warn)]',
      cardBorder: 'border-[color-mix(in_srgb,var(--timeline-rail-warn)_58%,var(--line)_42%)]',
      frameBorder: 'border-line',
      srStatus: '进行中',
    }
  }
  return {
    dot: 'border-[color-mix(in_srgb,var(--timeline-rail-success)_58%,var(--line)_42%)] bg-[color-mix(in_srgb,var(--timeline-rail-success)_26%,var(--panel)_74%)]',
    stem: 'bg-[var(--timeline-rail-success)]',
    cardBorder: 'border-[color-mix(in_srgb,var(--timeline-rail-success)_55%,var(--line)_45%)]',
    frameBorder: 'border-line',
    srStatus: '已完成',
  }
}
